import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, Typography } from '@mui/material';
import {
  Close as ClearIcon,
  Difference as CopyIcon,
  FileCopy as CopyAllIcon,
  Merge as UpdateIcon,
} from '@mui/icons-material';
import { orange } from '@mui/material/colors';

import { SpaceID } from '@playq/octopus-common';
import { AppEntityClass, Space } from '@playq/octopus2-apps';
import { AppPromotions } from '@playq/octopus2-apps-utility';

import {
  services2,
  unwrapEither,
  useAppSpacePromotionsClean,
  useAppSpacePromotionsClone,
  useAppSpacePromotionsCopy,
  useAppSpacePromotionsUpdate,
} from '/api';
import { appToolkit } from '/store';
import { snackbarService } from '/common/snackbarService';
import { confirmDialog, ConfirmDialogType } from '/common/ConfirmDialog';
import { TextAnimation } from '/shared/TextAnimation';
import { createPromotionSource } from '/helpers';

import { useSpaceSelectContext } from '../SpaceSelectContext';

import { ISpacePromotionsActionsProps } from './types';
import { getAvailableEntitiesToCopy } from './helpers';
import {
  ClonePromotionsTooltipTitle,
  CopyPromotionsTooltipTitle,
  CustomTooltip,
  UdpatePromotionsTooltipTitle,
} from './CustomTooltip';
import { usePromotionsEntitiesPopover } from './usePromotionsEntitiesPopover';

type SpaceSelectOpenReason = 'clone' | 'copy' | 'update';

export const SpacePromotionsActions = ({
  id,
  name,
  promotions,
  renderGenericTable,
  onClear,
}: ISpacePromotionsActionsProps) => {
  const appID = useSelector(appToolkit.selectors.appID);

  const [spaceSelectOpenReason, setSpaceSelectOpenReason] = useState<SpaceSelectOpenReason>();

  const { mutate: clonePromotions, isPending: isCloning } = useAppSpacePromotionsClone();

  const { mutate: copyPromotions, isPending: isCopying } = useAppSpacePromotionsCopy();

  const { mutate: clearAll } = useAppSpacePromotionsClean({
    onSuccess: () => {
      onClear();
      snackbarService.success('All the entities successfully demoted');
    },
  });

  const { mutate: updatePromotions, isPending: isUpdating } = useAppSpacePromotionsUpdate();

  const handleApplyEntitiesToCopy = (entities: AppEntityClass[]) => {
    if (entities.length > 0) {
      handleSpaceSelectOpen('copy')();
    }
  };

  const availableEntitiesToCopy = { [id.id]: getAvailableEntitiesToCopy(promotions) };

  const { PromotionsEntitiesPopover, entitiesToCopy, openEntitiesToCopySelect } = usePromotionsEntitiesPopover({
    availableEntitiesToCopy,
    onApply: handleApplyEntitiesToCopy,
  });

  const handleOpenEntitiesToCopy = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openEntitiesToCopySelect(event, id.id);
    },
    [openEntitiesToCopySelect, id.id]
  );

  const handleClone = useCallback(
    (targetSpaceID: SpaceID) => {
      if (appID) {
        confirmDialog({
          type: ConfirmDialogType.Warning,
          title: 'CLONE',
          content: (
            <>
              <Typography>
                This action will demote all entities from target space and then promote entities from the current space.
                If you want to promote entities without demoting existing, use &quot;Merge Promotions&quot; action. Make
                sure you have write permissions to all of the kinds of promoted entities.
              </Typography>
              <Typography>Are you sure you want to proceed?</Typography>
            </>
          ),
          onSuccess: () => clonePromotions({ appID, source: createPromotionSource(id), targetSpaceID }),
        });
      }
    },
    [appID, clonePromotions, id]
  );

  const handleCopy = useCallback(
    (targetSpaceID: SpaceID) => {
      if (appID) {
        confirmDialog({
          type: ConfirmDialogType.Warning,
          title: 'COPY',
          content: (
            <>
              <Typography>
                This action will demote all selected entities from target space and then promote selected entities. Make
                sure you have write permissions to all of the kinds of selected entities.
              </Typography>
              <Typography>Are you sure you want to proceed?</Typography>
            </>
          ),
          onSuccess: () =>
            copyPromotions({
              appID,
              source: createPromotionSource(id),
              targetSpaceID,
              entities: entitiesToCopy,
            }),
        });
      }
    },
    [appID, copyPromotions, id, entitiesToCopy]
  );

  const handleClear = useCallback(() => {
    if (appID) {
      confirmDialog({
        type: ConfirmDialogType.Warning,
        title: 'DEMOTE',
        content:
          'Are you sure you want to demote all the entities? Make sure you have write permissions to all of the kinds of promoted entities.',
        onSuccess: () => clearAll({ appID, spaceID: id }),
      });
    }
  }, [appID, clearAll, id]);

  const handleUpdate = useCallback(
    async (spaceID: SpaceID) => {
      if (!promotions || !appID) {
        return;
      }
      const source = createPromotionSource(spaceID);
      try {
        const targetPromotions = await unwrapEither(services2.spacesPromotionsService.appPromotions(appID, source));
        const updatedPromotions = new AppPromotions(targetPromotions.serialize());
        if (promotions.config !== undefined) {
          updatedPromotions.config = promotions.config;
        }
        if (promotions.segment !== undefined) {
          updatedPromotions.segment = promotions.segment;
        }
        updatedPromotions.experiments.push(...promotions.experiments);
        updatedPromotions.events.push(...promotions.events);
        updatedPromotions.flows.push(...promotions.flows);

        confirmDialog({
          type: ConfirmDialogType.Warning,
          title: 'MERGE PROMOTIONS',
          content:
            'Are you sure you want to promote all the entities to another space?  Make sure you have write permissions to all of the kinds of promoted entities.',
          onSuccess: () => updatePromotions({ spaceID, promotions: updatedPromotions }),
        });
      } catch (err) {
        snackbarService.genericFailure(err as Error);
      }
    },
    [appID, promotions, updatePromotions]
  );

  const handleSpaceSelectOpen = (reason: SpaceSelectOpenReason) => () => {
    setSpaceSelectOpenReason(reason);
  };

  const handleSpaceSelectClose = useCallback(() => {
    setSpaceSelectOpenReason(undefined);
  }, []);

  const handleSpaceSelect = useCallback(
    (reason: SpaceSelectOpenReason) => (space?: Space) => {
      if (space) {
        if (reason === 'clone') {
          handleClone(space.id);
        }
        if (reason === 'copy') {
          handleCopy(space.id);
        }
        if (reason === 'update') {
          handleUpdate(space.id);
        }
      }
      handleSpaceSelectClose();
    },
    [handleClone, handleCopy, handleSpaceSelectClose, handleUpdate]
  );

  const actions = useMemo(
    () => [
      {
        key: 'Merge Promotions',
        icon: <UpdateIcon />,
        title: <UdpatePromotionsTooltipTitle name={name} />,
        onClick: handleSpaceSelectOpen('update'),
      },
      {
        icon: <CopyIcon />,
        key: 'Copy',
        title: <CopyPromotionsTooltipTitle name={name} />,
        onClick: handleOpenEntitiesToCopy,
      },
      {
        key: 'Clone All',
        icon: <CopyAllIcon />,
        title: <ClonePromotionsTooltipTitle name={name} />,
        onClick: handleSpaceSelectOpen('clone'),
      },
      {
        key: 'Demote All',
        icon: <ClearIcon />,
        title: (
          <>
            <Typography variant='h6'>Demote All</Typography>
            <Typography>Demote all the entities from selected space.</Typography>
          </>
        ),
        onClick: handleClear,
      },
    ],
    [handleOpenEntitiesToCopy, handleClear, name]
  );

  const { renderSpaceSelect } = useSpaceSelectContext();

  if (isCloning || isCopying || isUpdating) {
    return <TextAnimation color={orange[500]}>Processing</TextAnimation>;
  }

  return (
    <div>
      {actions.map(({ icon, key, title, onClick }) => (
        <CustomTooltip title={title} key={key} enterDelay={1000}>
          <IconButton size='small' color='default' onClick={onClick}>
            {icon}
          </IconButton>
        </CustomTooltip>
      ))}

      {!!spaceSelectOpenReason &&
        renderSpaceSelect({
          activeSpaceID: id,
          renderGenericTable,
          onSelect: handleSpaceSelect(spaceSelectOpenReason),
          onClose: handleSpaceSelectClose,
        })}

      {PromotionsEntitiesPopover}
    </div>
  );
};
